
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { UserPlus, ExternalLink, AlertCircle } from "lucide-react";
import { Job } from '@/components/jobs/types';
import { useJobApplications } from '@/hooks/useJobApplications';
import { JobApplication, ApplicationStatus } from '@/types/application';
import { Link as RouterLink } from 'react-router-dom';
import { ApplicationStatusChanger } from './ApplicationStatusChanger';
import { AddApplicantDialog } from './AddApplicantDialog';

interface ApplicationsTabProps {
  job: Job;
}

const getStatusBadgeVariant = (status: ApplicationStatus) => {
  switch (status) {
    case 'hired':
      return 'success';
    case 'rejected':
    case 'withdrawn':
      return 'destructive';
    case 'offer_extended':
      return 'outline'; 
    case 'interviewing':
      return 'secondary';
    case 'reviewing':
    case 'submitted':
    default:
      return 'default';
  }
};

export const ApplicationsTab: React.FC<ApplicationsTabProps> = ({ job }) => {
  const applicantCount = job.applicants;
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: applications, isLoading, error, refetch } = useJobApplications(job.id);
  console.log("ApplicationsTab - isLoading:", isLoading, "error:", error, "applications:", applications);

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Applications for {job.title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            {typeof applicantCount === 'number' ? (
              <p className="text-sm text-muted-foreground">Total Applicants (Summary): {applicantCount}</p>
            ) : (
              <p className="text-sm text-muted-foreground">Applicant count summary not available.</p>
            )}
            {applications && <p className="text-sm text-muted-foreground">Detailed Applications Loaded: {applications.length}</p>}
          </div>
          <Button variant="outline" size="sm" onClick={() => setIsDialogOpen(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Add Applicant
          </Button>
        </div>
        
        <div className="border-t pt-4">
          <h3 className="text-lg font-medium mb-2">Applicant List</h3>
          {isLoading && (
            <div className="space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          )}
          {error && (
             <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error Loading Applications</AlertTitle>
              <AlertDescription>
                {error.message || "An unexpected error occurred."}
                <Button variant="link" onClick={() => refetch()} className="p-0 h-auto ml-2">Try again</Button>
              </AlertDescription>
            </Alert>
          )}
          {!isLoading && !error && applications && applications.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Applied On</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {applications.map((app: JobApplication) => (
                  <TableRow key={app.id}>
                    <TableCell>
                      {app.candidate ? `${app.candidate.first_name || ''} ${app.candidate.last_name || ''}`.trim() || 'N/A' : 'Unknown Candidate'}
                      {app.candidate?.email && <div className="text-xs text-muted-foreground">{app.candidate.email}</div>}
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusBadgeVariant(app.status)}>
                        {app.status.charAt(0).toUpperCase() + app.status.slice(1).replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(app.applied_at)}</TableCell>
                    <TableCell className="text-right space-x-1">
                      {app.candidate_id && (
                        <Button variant="ghost" size="icon" asChild title="View Candidate Profile">
                          <RouterLink to={`/candidates/${app.candidate_id}`}>
                            <ExternalLink className="h-4 w-4" />
                          </RouterLink>
                        </Button>
                      )}
                      <ApplicationStatusChanger
                        applicationId={app.id}
                        jobId={job.id}
                        currentStatus={app.status}
                        candidateName={app.candidate ? `${app.candidate.first_name || ''} ${app.candidate.last_name || ''}`.trim() : undefined}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {!isLoading && !error && (!applications || applications.length === 0) && (
            <div className="p-4 border rounded-md bg-muted/20 text-center">
              <p className="text-muted-foreground">
                No applications have been submitted for this job yet.
              </p>
            </div>
          )}
        </div>
      </CardContent>
      
      {/* Add Applicant Dialog */}
      <AddApplicantDialog 
        jobId={job.id} 
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSuccess={() => refetch()}
      />
    </Card>
  );
};
